import Cookies from 'js-cookie';

import type { ConsentStorageRepository } from '../domain/types';
import { ConsentChoice, ConsentStatus } from '../domain/types';

const CONSENT_COOKIE_NAME = 'healthcareDataConsent';
const CONSENT_COOKIE_EXPIRES = 365; // days
const CONSENT_COOKIE_DOMAIN = '.healthcare.com';

/**
 * Cookie-based adapter for storing and retrieving user consent preferences
 *
 * Stores consent data in a cookie named 'healthcareDataConsent' with:
 * - 365 day expiration
 * - Domain: .healthcare.com (accessible across subdomains)
 * - Secure flag enabled
 * - SameSite: Lax
 *
 * @example
 * ```typescript
 * // Get current consent status
 * const status = consentStorageAdapter.getConsentStatus();
 *
 * // Save user's choice
 * consentStorageAdapter.saveConsent('all_cookies');
 *
 * // Clear consent
 * consentStorageAdapter.clearConsent();
 * ```
 */
export const consentStorageAdapter: ConsentStorageRepository = {
  getConsentStatus: () => {
    const cookieValue = Cookies.get(CONSENT_COOKIE_NAME);

    if (!cookieValue) {
      return ConsentStatus.UNKNOWN;
    }

    if (cookieValue === 'all_cookies') {
      return ConsentStatus.ALL_COOKIES;
    }

    if (cookieValue === 'required_cookies') {
      return ConsentStatus.REQUIRED_COOKIES;
    }

    return ConsentStatus.UNKNOWN;
  },

  saveConsent: (choice: ConsentChoice) => {
    Cookies.set(CONSENT_COOKIE_NAME, choice, {
      expires: CONSENT_COOKIE_EXPIRES,
      domain: CONSENT_COOKIE_DOMAIN,
      path: '/',
      secure: true,
      sameSite: 'Lax',
    });
  },

  clearConsent: () => {
    Cookies.remove(CONSENT_COOKIE_NAME, {
      domain: CONSENT_COOKIE_DOMAIN,
      path: '/',
    });
  },
};
